<template>
  <v-card class="transactions-table">
    <v-card-title class="toolbar primary white--text">
      <h2>Vault Transactions</h2>
      <template v-for="status in ['error', 'queued', 'processing', 'pending']">
        <v-badge
          :key="status"
          overlap
          :color="getStatusColor(status)"
        >
          <template v-slot:badge>
            <span>{{ statusCount[status] }}</span>
          </template>
          <v-tooltip
            top
            dark
          >
            <template v-slot:activator="{ on }">
              <v-btn
                small
                icon
                outline
                dark
                round
                v-on="on"
                @click="filters.status = status"
              >
                <v-icon
                  dark
                  small
                >
                  {{ getStatusIcon(status) }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ status }}</span>
            <span v-if="status === 'error'">(Last <b><u>7</u></b> days count)</span>
            <span v-else>(all time)</span>
          </v-tooltip>
        </v-badge>
      </template>
      <v-spacer />
      <span class="ml-3 mr-2">Search:</span>
      <v-text-field
        v-model="search"
        class="ml-3 mr-2"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="extIdentifier | hash | userId | wallet | token | status |
          from | to | requestId | isError | amount | blockNr | errorDetails"
        single-line
        hide-details
      />
    </v-card-title>

    <div class="fab-container">
      <!-- FAB START -->
      <v-speed-dial
        v-model="fabActive"
        direction="left"
        open-on-hover
      >
        <template v-slot:activator>
          <v-btn
            v-model="fabActive"
            color="warning"
            fab
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
        </template>
        <v-btn
          color="success"
          round
          @click="createModalOpen = true"
        >
          <v-icon dark>
            add
          </v-icon>
          <span class="ml-2">Create</span>
        </v-btn>

        <v-btn
          color="secondary"
          round
          :loading="loading"
          @click="handleStartProcessingQueued()"
        >
          <v-icon dark>
            restart_alt
          </v-icon>
          <span class="ml-2">Start processing queued</span>
        </v-btn>
      </v-speed-dial>
      <!-- FAB END -->
    </div>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filters
        <v-tooltip
          v-if="!hasfilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
              @click="resetFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="filters.status"
        class="ml-5"
        primary
        :items="statusOptions"
        label="Status"
        item-value="value"
        clearable
      >
        <template v-slot:selection="{ item }">
          <v-chip
            dark
            :color="getStatusColor(item)"
          >
            <span>{{ item }}</span>
          </v-chip>
        </template>
      </v-select>
      <v-autocomplete
        v-model="filters.token"
        class="ml-5"
        :items="vaultAssetsTokens.map(
          token => ({text: `${token.name} - ${token.token}`, value: token.token})
        )"
        name="token"
        item-value="value"
        label="Token"
        clearable
      />
    </v-card-title>

    <v-data-table
      disable-initial-sort
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="getVaultTransactionsPagination.total"
      :loading="loading"
      :rows-per-page-items="getRangeItemsPerPage()"
      :expand="expand"
      :pagination="getVaultTransactionsPagination"
      class="elevation-1"
      item-key="extIdentifier"
      @update:pagination="onUpdatePagination"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-xs-center">
            {{ props.item.hash }}
            <v-chip
              v-if="props.item.hash"
              small
              outline
              class="wallet-chip"
            >
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <a
                    style="color:inherit"
                    :href="getEpirusExplorerURL(props.item.hash)"
                    target="_blank"
                    v-on="on"
                  >
                    <v-icon small>
                      link
                    </v-icon>
                  </a>
                </template>
                <span>View transaction on Epirus Explorer</span>
              </v-tooltip>
            </v-chip>
          </td>
          <td class="text-xs-center">
            {{ props.item.wallet }}
            <v-chip
              v-if="props.item.wallet"
              small
              outline
              class="wallet-chip"
            >
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <a
                    :href="`/#/vault/wallets?search=${props.item.wallet}`"
                    target="_blank"
                    style="color:inherit"
                    v-on="on"
                  >
                    <v-icon small>
                      account_balance_wallet
                    </v-icon>
                  </a>
                </template>
                <span>View wallet details</span>
              </v-tooltip>
            </v-chip>
          </td>
          <td class="text-xs-center">
            {{ props.item.toWallet }}
            <v-chip
              v-if="props.item.toWallet"
              small
              outline
              class="wallet-chip"
            >
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <a
                    style="color:inherit"
                    :href="`/#/vault/wallets?search=${props.item.toWallet}`"
                    target="_blank"
                    v-on="on"
                  >
                    <v-icon small>
                      account_balance_wallet
                    </v-icon>
                  </a>
                </template>
                <span>View wallet details</span>
              </v-tooltip>
            </v-chip>
          </td>
          <td class="text-xs-center">
            {{ props.item.extIdentifier }}
          </td>
          <td class="text-xs-center">
            {{ props.item.amount }}
          </td>
          <td class="text-xs-center">
            <v-chip
              outline
              small
            >
              {{ getTokenSymbol(props.item.token) }}

              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <a
                    style="color:inherit"
                    :href="`/#/vault/assets?search=${props.item.token}`"
                    target="_blank"
                    class="ml-1"
                    v-on="on"
                  >
                    <v-icon small>
                      business
                    </v-icon>
                  </a>
                </template>
                <span>View asset details</span>
              </v-tooltip>
            </v-chip>
          </td>
          <td class="text-xs-center">
            <span class="break-word">{{ $moment(props.item.timestamp).format('YYYY-MM-DD, hh:mm:ss a') }}</span>
          </td>
          <td class="text-xs-center">
            <v-chip
              :color="getStatusColor(props.item.status)"
              outline
              small
            >
              {{ props.item.status }}
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  :color="props.expanded ? 'blue darken-1' : ''"
                  @click="props.expanded = !props.expanded"
                  v-on="on"
                >
                  <v-icon small>
                    {{ props.expanded ? 'visibility_off' : 'visibility' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ props.expanded ? 'Close transaction details' : 'Open transaction details' }}</span>
            </v-tooltip>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  :color="props.expanded ? 'blue darken-1' : ''"
                  :disabled="!props.item.isError"
                  v-on="on"
                  @click="handleRequeueTransactionDialog(props.item)"
                >
                  <v-icon small>
                    {{ getStatusIcon('queued') }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Requeue transaction</span>
            </v-tooltip>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  :disabled="props.item.status !== 'confirmed'"
                  v-on="on"
                  @click="handleCreateRevertTransaction(props.item)"
                >
                  <v-icon small>
                    sync_alt
                  </v-icon>
                </v-btn>
              </template>
              <span>Revert transaction</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <VaultTransactionDetail
          :transaction="props.item"
        />
      </template>
    </v-data-table>


    <!-- CREATE TRANSACTION DIALOG -->
    <CreateVaultTransactionDialog
      :is-open="createModalOpen"
      :revert-transaction="revertTransaction"
      @onCloseDialog="onCloseCreateDialog()"
      @onCreateTransaction="onCreateTransaction()"
    />

    <!-- REQUEUE TRANSACTION DIALOG -->
    <RequeueTransactionDialog
      :is-open="requeueTransactionDialogOpen"
      :transaction="requeueTransaction"
      :loading="loading"
      @onCloseDialog="onCloseRequeueTransactionDialog()"
      @onCreateTransaction="onCreateTransaction()"
    />
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import VaultTransactionDetail from '@/components/vault/VaultTransactionDetail';
import RequeueTransactionDialog from '@/components/vault/dialog/RequeueTransactionDialog';
import CreateVaultTransactionDialog from '@/components/vault/dialog/CreateVaultTransactionDialog';
import { setQuery } from '@/helpers';

export default {
  name: 'TransactionsTable',
  components: {
    CreateVaultTransactionDialog,
    RequeueTransactionDialog,
    VaultTransactionDetail,
  },
  props: {
    transactions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      fabActive: false,
      revertTransaction: null,
      createModalOpen: false,
      requeueTransactionDialogOpen: false,
      requeueTransaction: null,
      search: '',
      headers: [
        {
          text: 'Hash',
          align: 'left',
          value: 'hash',
          sortable: false,
        },
        {
          text: 'From (Wallet Name)',
          align: 'left',
          value: 'wallet',
          sortable: false,
        },
        {
          text: 'To (Wallet Name)',
          align: 'left',
          value: 'toWallet',
          sortable: false,
        },
        {
          text: 'External Identifier',
          align: 'left',
          value: 'extIdentifier',
          sortable: false,
        },
        {
          text: 'Amount',
          align: 'left',
          value: 'amount',
          sortable: true,
        },
        {
          text: 'Token',
          align: 'left',
          value: 'token',
          sortable: false,
        },
        {
          text: 'Timestamp',
          align: 'left',
          value: 'timestamp',
          sortable: true,
        },
        {
          text: 'Status',
          align: 'left',
          value: 'status',
          sortable: false,
        },
        {
          text: 'Actions',
          align: 'left',
          value: 'actions',
          sortable: false,
        },
      ],
      filters: {
        status: null,
        token: null,
      },
      // move status options to enum?
      statusOptions: [
        'confirmed',
        'error',
        'stuck',
        'queued',
        'pending',
        'processing',
      ],
    };
  },
  computed: {
    ...mapGetters([
      'getVaultTransactionsPagination',
      'statusCount',
      'vaultAssetsTokens',
    ]),
    filteredResults() {
      return this.transactions;
    },

    hasfilters() {
      return !!this.filters.status
        || !!this.filters.token;
    },
  },
  watch: {
    search: {
      immediate: false,
      handler(search) {
        setQuery(
          {
            ...this.$route.query,
            page: 1,
            ...this.filters,
            search,
          },
          this.$route,
          this.$router,
        );
      },
    },
    filters: {
      immediate: false,
      deep: true,
      handler(filters) {
        setQuery(
          {
            ...this.$route.query,
            page: 1,
            ...filters,
            search: this.search,
          },
          this.$route,
          this.$router,
        );
      },
    },
  },
  beforeMount() {
    this.filters = {
      status: this.$route.query.status,
      token: this.$route.query.token,
    };

    this.search = this.$route.query.search;
  },
  methods: {
    onCloseCreateDialog() {
      this.createModalOpen = false;
      this.revertTransaction = null;
    },
    onCreateTransaction() {
      // wait enough for open search to get new transactions
      setTimeout(this.refreshTransactions, 1000);
    },
    onCloseRequeueTransactionDialog() {
      this.requeueTransactionDialogOpen = false;
      this.requeueTransaction = null;
    },
    async handleRequeueTransactionDialog(item) {
      this.requeueTransaction = item;
      this.requeueTransactionDialogOpen = true;
    },
    async handleCreateRevertTransaction(transaction) {
      this.revertTransaction = transaction;
      this.createModalOpen = true;
      this.refreshTransactions();
    },
    handleStartProcessingQueued() {
      this.startProcessingQueuedTransactions();
      this.refreshTransactions();
    },
    onUpdatePagination(pagination) {
      setQuery(
        {
          ...this.$route.query,
          ...this.filters,
          search: this.search,
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
        },
        this.$route,
        this.$router,
      );
    },
    getEpirusExplorerURL(hash) {
      return `http://ec2-3-76-104-0.eu-central-1.compute.amazonaws.com/transactions/${hash}`;
    },
    getTokenSymbol(token) {
      const asset = this.vaultAssetsTokens.find(item => item.token === token);

      return asset ? asset.name : token;
    },
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'confirmed':
          return 'success';

        case 'error':
        case 'stuck':
          return 'error';

        case 'queued':
        case 'pending':
          return 'warning';

        case 'processing':
          return 'info';

        default:
          return '';
      }
    },
    getStatusIcon(status) {
      switch (status.toLowerCase()) {
        case 'confirmed':
          return 'done';

        case 'error':
        case 'stuck':
          return 'error';

        case 'queued':
          return 'sync';
        case 'pending':
          return 'cloud_sync';

        case 'processing':
          return 'start';

        default:
          return '';
      }
    },
    ...mapActions([
      'setVaultTransactionsPagination',
      'setLimitItemsPerPage',
      'getVaultAssets',
      'reset',
      'startProcessingQueuedTransactions',
    ]),
    getRangeItemsPerPage() {
      return _.range(25, 101, 25);
    },
    resetFilters() {
      this.filters = _.mapValues(this.filters, () => null);
    },

    refreshTransactions() {
      this.$emit('refreshTransactions');
    },
  },
};
</script>

<style lang="scss" scoped>
.v-menu__content {
  min-width: auto !important;
}

.fab-container {
  position: relative;
  .v-speed-dial {
    position: absolute;
    right: 0;
    top: -34px;
    z-index: 2;
  }
}

.transactions-table {
  a {
    text-decoration: none;
    color: black;
  }
  .break-word {
    word-break: break-word;
  }
  .toolbar {
    position: relative;
    background-color: #1a4c7d;
    color: white;
    padding: 24px 24px 16px 24px;
  }
  .v-card__title {
    justify-content: flex-start;
    padding: 24px;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .primary--text {
    color: white !important;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
    flex: 1;
  }

  table.v-table {
    td {
      word-break: break-all;
    }
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vault/VaultTransactionsTable.vue